'use client'

import { AnimatePresence, MotionConfig, motion } from 'motion/react'
import { useEffect, useRef, useState, type ComponentType, type KeyboardEvent, type ReactNode, type SVGProps } from 'react'
import { toast } from 'sonner'

import { Button } from '@components/ui/button'
import { cn } from '@lib/cn'
import { DOCS_URL, HOMEPAGE_URL, PRIVACY_URL, REFUNDS_URL, SUPPORT_EMAIL, TERMS_URL } from '@lib/constants'
import { closeBeacon, isBeaconOpen, loadBeacon } from '@lib/help-scout-beacon'
import { getErrorMessage } from '@lib/trpc'
import { useAppRoute } from '@lib/use-app-route'
import { useOffsetHeight } from '@lib/use-offset-height'
import { useMe } from '@resolvers/auth'
import { useSendSupportMessageMutation, useSupportBeacon } from '@resolvers/support'
import { SUPPORT_RATINGS, type SupportRating } from '@server/orbit/helpers/support'
import IconRotateLeft from '@images/icons/arrow-rotate-left.svg'
import IconChevronLeft from '@images/icons/chevron-left.svg'
import IconChevronRight from '@images/icons/chevron-right.svg'
import IconInfo from '@images/icons/circle-info.svg'
import IconCard from '@images/icons/credit-card.svg'
import IconEnvelope from '@images/icons/envelope.svg'
import IconFile from '@images/icons/file-content.svg'
import IconHeart from '@images/icons/heart.svg'
import IconLock from '@images/icons/lock.svg'
import IconQuestion from '@images/icons/question-mark.svg'
import IconScroll from '@images/icons/scroll.svg'
import IconShield from '@images/icons/shield.svg'
import IconSend from '@images/icons/square-arrow-up.svg'

type View = 'menu' | 'feedback' | 'message' | 'about'
type Icon = ComponentType<SVGProps<SVGSVGElement>>

const RATING_EMOJI: Record<SupportRating, string> = {
  'very happy': '1f929',
  happy: '1f600',
  sad: '1f615',
  'very sad': '1f62d'
}

const DEFAULT_RATING: SupportRating = 'happy'

const PANEL_TRANSITION = { duration: 0.15, ease: 'easeOut' } as const
const VIEW_TRANSITION = { duration: 0.08, ease: 'easeOut' } as const
const HEIGHT_EASE = [0.16, 1, 0.3, 1] as const

const aboutLinks: { label: string; href: string; icon: Icon }[] = [
  { label: 'Pricing', href: `${HOMEPAGE_URL}/pricing`, icon: IconCard },
  { label: 'Security', href: `${HOMEPAGE_URL}/security`, icon: IconShield },
  { label: 'Privacy', href: PRIVACY_URL, icon: IconLock },
  { label: 'Terms', href: TERMS_URL, icon: IconFile },
  { label: 'Refunds', href: REFUNDS_URL, icon: IconRotateLeft }
]

const FOCUSABLE = 'a[href], button:not(:disabled), input, textarea'

const rowClassName =
  'flex h-[34px] w-full items-center gap-2.5 px-4 text-start text-sm text-o-ink outline-hidden hover:bg-o-chip focus-visible:bg-o-chip'

const RowIcon = ({ icon: Icon }: { icon: Icon }) => <Icon aria-hidden className="size-4 shrink-0 text-o-muted" />

const Chevron = () => <IconChevronRight aria-hidden className="size-3 shrink-0 text-o-faint opacity-70 rtl:rotate-180" />

const LinkRow = ({ href, icon, onClick, children }: { href: string; icon: Icon; onClick: () => void; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className={rowClassName}>
    <RowIcon icon={icon} />
    <span className="flex-1">{children}</span>
    <Chevron />
  </a>
)

const ButtonRow = ({
  icon,
  onClick,
  trailing,
  children
}: {
  icon: Icon
  onClick: () => void
  trailing?: ReactNode
  children: ReactNode
}) => (
  <button type="button" onClick={onClick} className={cn('group', rowClassName)}>
    <RowIcon icon={icon} />
    <span className="flex-1">{children}</span>
    {trailing ?? <Chevron />}
  </button>
)

const BackRow = ({ onClick, autoFocus, children }: { onClick: () => void; autoFocus?: boolean; children: ReactNode }) => (
  <button
    type="button"
    autoFocus={autoFocus}
    onClick={onClick}
    className="flex h-10 w-full items-center gap-2.5 border-b border-o-line px-4 text-start text-sm text-o-slate outline-hidden hover:bg-o-chip hover:text-o-ink focus-visible:bg-o-chip"
  >
    <span className="flex size-4 shrink-0 items-center justify-center">
      <IconChevronLeft aria-hidden className="size-3.5 text-o-muted rtl:rotate-180" />
    </span>
    <span className="flex-1">{children}</span>
  </button>
)

const MessageField = ({
  value,
  onChange,
  onSubmit,
  label,
  placeholder,
  inputRef
}: {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  label: string
  placeholder: string
  inputRef: React.RefObject<HTMLTextAreaElement | null>
}) => (
  <textarea
    ref={inputRef}
    autoFocus
    value={value}
    onChange={(event) => onChange(event.target.value)}
    onKeyDown={(event) => {
      if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return
      event.preventDefault()
      onSubmit()
    }}
    aria-label={label}
    placeholder={placeholder}
    className="block h-[104px] w-full resize-none border-0 bg-transparent px-4 py-3 text-sm leading-[21px] text-o-ink outline-hidden placeholder:text-o-faint focus:ring-0"
  />
)

const nextFocusIndex = (key: string, current: number, count: number) => {
  if (key === 'Home') return 0
  if (key === 'End') return count - 1
  if (key === 'ArrowDown') return current < 0 ? 0 : (current + 1) % count
  return current <= 0 ? count - 1 : current - 1
}

const HelpWidget = () => {
  const { pathname, team } = useAppRoute()
  const { me } = useMe()
  const user = me?.user
  const [isOpen, setIsOpen] = useState(false)
  const [view, setView] = useState<View>('menu')
  const [question, setQuestion] = useState('')
  const [message, setMessage] = useState('')
  const [feedback, setFeedback] = useState('')
  const [rating, setRating] = useState<SupportRating | null>(DEFAULT_RATING)
  const [isOpeningBeacon, setIsOpeningBeacon] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const askAttemptRef = useRef(0)
  const [contentRef, contentHeight, isFirstMeasure] = useOffsetHeight()
  const { getBeaconId } = useSupportBeacon(isOpen)
  const { sendSupportMessage, sendSupportMessageIsPending } = useSendSupportMessageMutation()

  const close = () => {
    askAttemptRef.current += 1
    setIsOpeningBeacon(false)
    setIsOpen(false)
    setView('menu')
  }

  useEffect(() => {
    if (!isOpen) return
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (wrapperRef.current?.contains(event.target as Node)) return
      askAttemptRef.current += 1
      setIsOpeningBeacon(false)
      setIsOpen(false)
      setView('menu')
    }
    document.addEventListener('pointerdown', closeOnOutsidePress)
    return () => document.removeEventListener('pointerdown', closeOnOutsidePress)
  }, [isOpen])

  const closeAndReturnFocus = () => {
    close()
    triggerRef.current?.focus()
  }

  const toggle = () => {
    if (isBeaconOpen()) {
      closeBeacon()
      return
    }
    if (isOpen) close()
    else setIsOpen(true)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) return
    if (event.key === 'Escape') {
      event.preventDefault()
      if (view === 'menu') closeAndReturnFocus()
      else setView('menu')
      return
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
    if (event.target instanceof HTMLTextAreaElement) return
    if (event.target instanceof HTMLInputElement && (event.key === 'Home' || event.key === 'End')) return
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
    if (items.length === 0) return
    event.preventDefault()
    const current = items.indexOf(document.activeElement as HTMLElement)
    items[nextFocusIndex(event.key, current, items.length)].focus()
  }

  const copyEmail = async () => {
    close()
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL)
      toast.success(`Copied ${SUPPORT_EMAIL}`)
    } catch {
      toast(`Email us at ${SUPPORT_EMAIL}`)
    }
  }

  const openBeacon = async (text: string, isCurrent: () => boolean) => {
    const beaconId = await getBeaconId().catch(() => null)
    if (!beaconId || !isCurrent()) return false
    const beacon = await loadBeacon(beaconId)
    if (!beacon || !isCurrent()) return false
    if (user?.email) beacon('identify', { email: user.email, name: user.name ?? undefined })
    beacon('session-data', { Team: team ? `${team.name} (${team.slug})` : 'none', Page: pathname })
    if (text) beacon('prefill', { subject: text.split('\n')[0].slice(0, 80), text })
    beacon('open')
    beacon('navigate', '/ask/message/')
    return true
  }

  const ask = async () => {
    if (isOpeningBeacon) return
    const text = question.trim()
    const attempt = ++askAttemptRef.current
    const isCurrent = () => askAttemptRef.current === attempt
    setIsOpeningBeacon(true)
    const opened = await openBeacon(text, isCurrent)
    if (!isCurrent()) return
    setIsOpeningBeacon(false)
    if (opened) {
      setQuestion('')
      close()
      return
    }
    setMessage(text)
    setView('message')
  }

  const send = async (input: { kind: 'question' | 'feedback'; message: string; rating?: SupportRating }) => {
    if (sendSupportMessageIsPending) return false
    try {
      await sendSupportMessage({ ...input, page: pathname, teamId: team?.id })
      close()
      return true
    } catch (error) {
      toast.error(getErrorMessage(error))
      return false
    }
  }

  const sendMessage = async () => {
    if (!message.trim()) {
      textareaRef.current?.focus()
      return
    }
    if (!(await send({ kind: 'question', message }))) return
    toast.success(`Sent. We'll reply to ${user?.email ?? 'your email'}.`)
    setMessage('')
    setQuestion('')
  }

  const sendFeedback = async () => {
    if (!feedback.trim() && !rating) {
      textareaRef.current?.focus()
      return
    }
    if (!(await send({ kind: 'feedback', message: feedback, rating: rating ?? undefined }))) return
    toast.success('Thanks, we read every one.')
    setFeedback('')
    setRating(DEFAULT_RATING)
  }

  const hasQuestion = question.trim().length > 0
  const heightDuration = isFirstMeasure ? 0 : 0.32

  return (
    <MotionConfig reducedMotion="user">
      <div ref={wrapperRef} onKeyDown={onKeyDown} className="fixed bottom-3 end-3 z-40 font-sans md:bottom-6 md:end-5">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="panel"
              ref={panelRef}
              id="help-widget-panel"
              role="dialog"
              aria-label="Help"
              initial={{ opacity: 0, scale: 0.97, y: 4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 4 }}
              transition={PANEL_TRANSITION}
              className="absolute bottom-[42px] end-0 w-[272px] origin-bottom-right overflow-hidden rounded-2xl border border-o-line-strong bg-o-card shadow-o-pane rtl:origin-bottom-left"
            >
              <motion.div
                initial={false}
                animate={{ height: contentHeight || 'auto' }}
                transition={{ duration: heightDuration, ease: HEIGHT_EASE }}
                className="overflow-hidden"
              >
                <div ref={contentRef}>
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={view}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={VIEW_TRANSITION}
                    >
                      {view === 'menu' && (
                        <>
                          <div className="py-2">
                            <LinkRow href={DOCS_URL} icon={IconScroll} onClick={close}>
                              Docs
                            </LinkRow>
                            <ButtonRow icon={IconHeart} onClick={() => setView('feedback')}>
                              Send feedback
                            </ButtonRow>
                            <ButtonRow
                              icon={IconEnvelope}
                              onClick={copyEmail}
                              trailing={
                                <span className="invisible shrink-0 text-[12.5px] text-o-muted group-hover:visible group-focus-visible:visible">
                                  {SUPPORT_EMAIL}
                                </span>
                              }
                            >
                              Email us
                            </ButtonRow>
                            <ButtonRow icon={IconInfo} onClick={() => setView('about')}>
                              More info
                            </ButtonRow>
                          </div>
                          <div className="flex h-12 items-center gap-2 border-t border-o-line pe-3 ps-4">
                            <input
                              autoFocus
                              type="text"
                              value={question}
                              onChange={(event) => setQuestion(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key !== 'Enter') return
                                event.preventDefault()
                                ask()
                              }}
                              aria-label="Ask a question"
                              placeholder="Ask a question…"
                              className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-o-ink outline-hidden placeholder:text-o-faint focus:ring-0"
                            />
                            <button
                              type="button"
                              aria-label="Send question"
                              onClick={ask}
                              disabled={isOpeningBeacon}
                              className={cn(
                                'flex size-8 shrink-0 items-center justify-center rounded-md outline-hidden transition-colors duration-75 ease-out focus-visible:ring-1 focus-visible:ring-o-line-strong',
                                hasQuestion ? 'text-o-ink' : 'text-o-line-strong hover:text-o-faint'
                              )}
                            >
                              <IconSend aria-hidden className="size-7" />
                            </button>
                          </div>
                        </>
                      )}

                      {view === 'about' && (
                        <>
                          <BackRow onClick={() => setView('menu')} autoFocus>
                            More info
                          </BackRow>
                          <div className="py-2">
                            {aboutLinks.map(({ label, href, icon }) => (
                              <LinkRow key={label} href={href} icon={icon} onClick={close}>
                                {label}
                              </LinkRow>
                            ))}
                          </div>
                        </>
                      )}

                      {view === 'feedback' && (
                        <>
                          <BackRow onClick={() => setView('menu')}>Send feedback</BackRow>
                          <MessageField
                            inputRef={textareaRef}
                            value={feedback}
                            onChange={setFeedback}
                            onSubmit={sendFeedback}
                            label="Feedback"
                            placeholder="What should we improve?"
                          />
                          <div className="flex items-center justify-between px-3 pb-3">
                            <div className="flex items-center gap-0.5">
                              {SUPPORT_RATINGS.map((value) => (
                                <button
                                  key={value}
                                  type="button"
                                  aria-label={value}
                                  aria-pressed={rating === value}
                                  onClick={() => setRating(rating === value ? null : value)}
                                  className={cn(
                                    'flex size-8 items-center justify-center rounded-full outline-hidden duration-150 ease-out hover:scale-110 focus-visible:ring-1 focus-visible:ring-o-line-strong motion-safe:transition-transform',
                                    rating === value && 'scale-110 bg-o-line dark:bg-o-chip'
                                  )}
                                >
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={`https://assets.vercel.com/twemoji/${RATING_EMOJI[value]}.svg`}
                                    alt=""
                                    width={20}
                                    height={20}
                                    decoding="async"
                                  />
                                </button>
                              ))}
                            </div>
                            <Button size="xs" onClick={sendFeedback} loading={sendSupportMessageIsPending}>
                              Send
                            </Button>
                          </div>
                        </>
                      )}

                      {view === 'message' && (
                        <>
                          <BackRow onClick={() => setView('menu')}>Message us</BackRow>
                          <MessageField
                            inputRef={textareaRef}
                            value={message}
                            onChange={setMessage}
                            onSubmit={sendMessage}
                            label="Message"
                            placeholder="How can we help?"
                          />
                          <div className="flex items-center justify-between gap-3 pb-3 pe-3 ps-4">
                            <div className="flex min-w-0 flex-col text-[12.5px] leading-4">
                              <span className="text-o-muted">We reply to</span>
                              <span className="truncate text-o-ink">{user?.email}</span>
                            </div>
                            <Button size="xs" onClick={sendMessage} loading={sendSupportMessageIsPending}>
                              Send
                            </Button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <button
          ref={triggerRef}
          type="button"
          aria-label="Help"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls="help-widget-panel"
          onClick={toggle}
          className={cn(
            'flex size-9 items-center justify-center rounded-full bg-o-ink text-white shadow-lg outline-hidden transition duration-75 ease-out hover:opacity-90 focus-visible:ring-2 focus-visible:ring-o-line-strong focus-visible:ring-offset-2 focus-visible:ring-offset-o-panel dark:border dark:border-o-line-strong dark:bg-o-chip',
            isOpen && 'opacity-90'
          )}
        >
          <IconQuestion aria-hidden className="size-4" />
        </button>
      </div>
    </MotionConfig>
  )
}

export default HelpWidget
